export interface TrackingStat {
  label: string;
  value: string;
  valueColor: string;
  iconBg: string;
  iconColor: string;
}

export const CATEGORY_OPTIONS = ['All Categories', 'Driver', 'Sales Rep', 'Warehouse'] as const;
export const ROUTE_OPTIONS = ['All Routes'] as const;

export type PersonnelRole = 'Driver' | 'Sales Rep' | 'Warehouse';
export type PersonnelStatus = 'Driving' | 'At Store' | 'Idle' | 'Offline';

export interface Personnel {
  name: string;
  initials: string;
  avatarBg: string;
  role: PersonnelRole;
  route: string;
  stopsCompleted: number;
  totalStops: number;
  status: PersonnelStatus;
  lastSeen: string;
}

export const roleStyles: Record<PersonnelRole, { bg: string; color: string }> = {
  'Driver':    { bg: '#EFF6FF', color: '#2563EB' },
  'Sales Rep': { bg: '#FFF7ED', color: '#EA580C' },
  'Warehouse': { bg: '#F3F4F6', color: '#6B7280' },
};

export const statusStyles: Record<PersonnelStatus, { dot: string; label: string }> = {
  Driving:    { dot: '#2563EB', label: '#2563EB' },
  'At Store': { dot: '#16A34A', label: '#16A34A' },
  Idle:       { dot: '#D97706', label: '#D97706' },
  Offline:    { dot: '#9CA3AF', label: '#9CA3AF' },
};

export interface MapMarker {
  id: string;
  initials: string;
  label: string;
  status: PersonnelStatus;
  bg: string;
  top: string;
  left: string;
}

export interface RouteStop { num: string; top: string; left: string }

export type ActivitySeverity = 'warning' | 'error' | 'info' | 'success';

export interface ActivityEvent {
  message: string;
  time: string;
  severity: ActivitySeverity;
}

export const severityStyles: Record<ActivitySeverity, { dot: string; msgColor: string }> = {
  warning: { dot: '#D97706', msgColor: '#92400E' },
  error:   { dot: '#DC2626', msgColor: '#DC2626' },
  info:    { dot: '#6B7280', msgColor: '#374151' },
  success: { dot: '#16A34A', msgColor: '#15803D' },
};

const AVATAR_BGS = ['#BFDBFE', '#FDE68A', '#99F6E4', '#D1FAE5', '#FECACA', '#DDD6FE'];

export function initialsOf(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((n) => n[0]?.toUpperCase() ?? '').join('') || '?';
}

export function avatarBgFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_BGS[hash % AVATAR_BGS.length];
}

export function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
